// ============================================================
// sheetSearch - which sections a typed word is found in.
//
// The sheet is long and half of it starts folded, so the masthead's
// search narrows it to the sections that hold what was typed: a field's
// label, a catalog name, or something the combatant actually has.
// ============================================================
import type { Character } from '../../engine/character/types';
import { CHARACTERISTICS } from '../../engine/character/sheetPatches';
import { SKILL_CATALOG } from '../../engine/character/skillCatalog';
import { talentText, traitText } from '../../engine/character/instanceText';
import type { SheetSectionId } from '../../engine/roster/pins';

/** The words every sheet's sections answer to, whoever the combatant is. */
const FIXED_WORDS: Record<SheetSectionId, string> = {
  characteristics: `characteristics unnatural bonus ${CHARACTERISTICS.map((c) => `${c.name} ${c.short}`).join(' ')}`,
  vitals: 'vitals wounds magnitude psy rating fear cover',
  armour: 'armour armor body head arm leg vehicle facing front side rear amorphous mass horde humanoid issued',
  defence: 'defence defense dodge parry agility ws force field power field deflect shot mixed extra',
  movement: 'movement move half full charge run size token',
  traits: 'traits xenos',
  talents: 'talents',
  skills: `skills ${SKILL_CATALOG.map((s) => s.name).join(' ')}`,
  stances: 'stances stance always on effects group',
};

/** What this combatant adds: the names of the traits and talents it has. */
function ownWords(character: Character): Partial<Record<SheetSectionId, string>> {
  return {
    traits: (character.traits ?? []).map((t) => traitText(t)?.name ?? t.traitId).join(' '),
    talents: (character.talents ?? []).map((t) => talentText(t)?.name ?? t.talentId).join(' '),
    stances: [
      ...(character.alwaysOn ?? []).map((a) => a.name),
      ...(character.soloModes ?? []).flatMap((m) => [m.group?.name ?? '', m.name]),
    ].join(' '),
  };
}

/**
 * The sections that hold `query`, or null for "no search": every word
 * typed must be found in the section, in any order.
 */
export function sectionsMatching(query: string, character: Character): Set<SheetSectionId> | null {
  const needles = query.toLowerCase().split(/\s+/).filter(Boolean);
  if (needles.length === 0) return null;
  const own = ownWords(character);
  const ids = Object.keys(FIXED_WORDS) as SheetSectionId[];
  const hit = ids.filter((id) => {
    const words = `${FIXED_WORDS[id]} ${own[id] ?? ''}`.toLowerCase();
    return needles.every((needle) => words.includes(needle));
  });
  return new Set(hit);
}
